/**
 * Step 3 — Destination.
 *
 * Where the admin decides which collection imported records land in.
 * Two paths: pick an existing collection, or set up a new one inline.
 *
 * The collection is NOT created at this step: collection creation happens
 * transactionally with import job creation at final Save (Step 5), so a
 * failed save doesn't leave an orphan collection. This step just collects
 * the user's intent.
 *
 * Destination config shape persisted into wizard state:
 *   mode: 'existing'
 *     collection_id: string
 *   mode: 'new'
 *     collection_name: string
 *     complaint_detection_enabled: boolean
 *     vulnerability_detection_enabled: boolean
 *
 * A collection another monitor already holds is listed disabled, with that
 * monitor's name, and a new name that would reuse one blocks Continue: the
 * backend allows one monitor per collection.
 *
 * Field names mirror the backend contract enforced in
 * `good-outcomes-api/app/services/import_jobs.py` validate_draft + the
 * `good-outcomes-collections` skill's `import_to_collection` config_schema
 * (`collection_name`, NOT `name`; `mode` must be persisted explicitly even
 * on the default-existing path so Save validation doesn't reject it).
 */
import React, { useEffect, useMemo } from 'react';

import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Switch } from '../../../ui/switch';
import { useCollections } from '../../../../hooks/useRecords';
import type { Collection } from '../../../../services/types';

type DestinationMode = 'existing' | 'new';

interface DestinationStepProps {
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  onBack: () => void;
  onContinue: () => void;
}

const getMode = (values: Record<string, unknown>): DestinationMode =>
  values.mode === 'new' ? 'new' : 'existing';

const getString = (values: Record<string, unknown>, key: string): string => {
  const v = values[key];
  return typeof v === 'string' ? v : '';
};

const getBoolean = (
  values: Record<string, unknown>,
  key: string,
  fallback: boolean,
): boolean => {
  const v = values[key];
  return typeof v === 'boolean' ? v : fallback;
};

const DestinationStep: React.FC<DestinationStepProps> = ({
  values,
  onChange,
  onBack,
  onContinue,
}) => {
  const { data: collectionList, isPending, isError, error } = useCollections();
  // Imports can only target collections created via the import path
  // (DatasetType.IMPORT on the backend — see import_jobs.py validate_draft).
  // The picker filters to those rows so the user can't pick a "default"
  // collection and hit the backend's defense-in-depth rejection.
  const collections: Collection[] = (collectionList?.collections ?? []).filter(
    (c) => c.type === 'import',
  );
  const hasCollections = collections.length > 0;

  const mode = getMode(values);
  const collectionId = getString(values, 'collection_id');
  const collectionName = getString(values, 'collection_name');
  const complaintDetection = getBoolean(
    values,
    'complaint_detection_enabled',
    true,
  );
  const vulnerabilityDetection = getBoolean(
    values,
    'vulnerability_detection_enabled',
    true,
  );

  // Persist `mode` explicitly even on the default-existing path so the
  // backend validator doesn't reject Save with "mode must be 'new' or
  // 'existing'". The local default in getMode() resolves what to *render*;
  // wizard state still needs the explicit value to send to the API.
  useEffect(() => {
    if (values.mode === undefined) {
      onChange({ mode: 'existing' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A new name blocks only when the collection the save would reuse is
  // monitored: the oldest import collection with exactly that name, as the
  // save's lookup picks it among legacy duplicates.
  const trimmedName = collectionName.trim();
  const reused = trimmedName
    ? collections
        .filter((c) => c.name === trimmedName)
        .reduce<Collection | undefined>(
          (oldest, c) =>
            !oldest || Date.parse(c.createdAt) < Date.parse(oldest.createdAt)
              ? c
              : oldest,
          undefined,
        )
    : undefined;
  const nameHolder = reused?.source ? reused : undefined;
  const pickedIsMonitored = Boolean(
    collections.find((c) => c.id === collectionId)?.source,
  );

  const canContinue = useMemo(() => {
    if (mode === 'existing') return collectionId.length > 0 && !pickedIsMonitored;
    if (mode === 'new') return trimmedName.length > 0 && !nameHolder;
    return false;
  }, [mode, collectionId, pickedIsMonitored, trimmedName, nameHolder]);

  const setMode = (next: DestinationMode) => {
    if (next === 'existing') {
      onChange({
        mode: 'existing',
        // Clear new-mode fields so they don't leak into a final submit if
        // the user toggles back; collection_id stays for nice resume.
        collection_name: '',
        complaint_detection_enabled: undefined,
        vulnerability_detection_enabled: undefined,
      });
    } else {
      onChange({
        mode: 'new',
        collection_id: '',
        // Default detection toggles on — matches the spec's posture that
        // new collections get complaint + vulnerability detection by default.
        complaint_detection_enabled: complaintDetection,
        vulnerability_detection_enabled: vulnerabilityDetection,
      });
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">Pick a destination</h2>
        <p className="text-sm text-slate-600">
          Choose which collection imported records land in.
        </p>
      </header>

      <RadioGroup
        value={mode}
        onValueChange={(v) => setMode(v as DestinationMode)}
        className="gap-3"
      >
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <RadioGroupItem value="existing" id="dest-mode-existing" className="mt-0.5" />
          <Label htmlFor="dest-mode-existing" className="flex-1 cursor-pointer space-y-0.5">
            <span className="text-sm font-medium text-slate-900">Existing collection</span>
            <span className="block text-xs text-slate-500">
              Add this import to a collection you already have.
            </span>
          </Label>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <RadioGroupItem value="new" id="dest-mode-new" className="mt-0.5" />
          <Label htmlFor="dest-mode-new" className="flex-1 cursor-pointer space-y-0.5">
            <span className="text-sm font-medium text-slate-900">New collection</span>
            <span className="block text-xs text-slate-500">
              Create a collection during save with the settings you choose here.
            </span>
          </Label>
        </div>
      </RadioGroup>

      {mode === 'existing' ? (
        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-4">
          <Label htmlFor="dest-collection-id">
            Collection <span className="ml-1 text-red-500">*</span>
          </Label>
          {isPending ? (
            <p className="text-xs text-slate-500">Loading collections…</p>
          ) : isError ? (
            <p className="text-xs text-red-600">
              Could not load collections:{' '}
              {error instanceof Error ? error.message : 'unknown error'}
            </p>
          ) : !hasCollections ? (
            <p className="text-xs text-slate-500">
              No import collections yet — switch to "New collection" above
              to create one.
            </p>
          ) : (
            <Select
              value={collectionId || ''}
              onValueChange={(v) => onChange({ collection_id: v })}
            >
              <SelectTrigger id="dest-collection-id">
                <SelectValue placeholder="Pick a collection…" />
              </SelectTrigger>
              <SelectContent>
                {collections.map((c) => (
                  <SelectItem key={c.id} value={c.id} disabled={c.source != null}>
                    {c.source
                      ? `${c.name} (monitored by ${c.source.monitorName})`
                      : c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : null}

      {mode === 'new' ? (
        <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-4">
          <div className="space-y-1.5">
            <Label htmlFor="dest-new-name">
              Collection name <span className="ml-1 text-red-500">*</span>
            </Label>
            <Input
              id="dest-new-name"
              value={collectionName}
              onChange={(e) => onChange({ collection_name: e.target.value })}
              placeholder="e.g. Intercom — Claims"
              aria-invalid={nameHolder ? true : undefined}
              aria-describedby={nameHolder ? 'dest-new-name-error' : undefined}
            />
            {nameHolder?.source ? (
              <p id="dest-new-name-error" className="text-xs text-red-600">
                {`A collection named "${trimmedName}" already exists and is monitored by ${nameHolder.source.monitorName}. Choose another name.`}
              </p>
            ) : null}
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <Label htmlFor="dest-new-complaint">Complaint detection</Label>
              <p className="text-xs text-slate-500">
                Classify incoming records for eligible complaints.
              </p>
            </div>
            <Switch
              id="dest-new-complaint"
              checked={complaintDetection}
              onCheckedChange={(checked) =>
                onChange({ complaint_detection_enabled: checked })
              }
            />
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <Label htmlFor="dest-new-vulnerability">Vulnerability detection</Label>
              <p className="text-xs text-slate-500">
                Flag potential consumer vulnerability on incoming records.
              </p>
            </div>
            <Switch
              id="dest-new-vulnerability"
              checked={vulnerabilityDetection}
              onCheckedChange={(checked) =>
                onChange({ vulnerability_detection_enabled: checked })
              }
            />
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default DestinationStep;
